import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/store";

import SidepeekDetailsAccordion from "../../../components/ui/accordion/v2/SidepeekDetailsAccordion";
import EditableField from "../../../components/ui/formelement/v2/EditableField";

import {
  BorrowerConstitution,
  BORROWER_CONSTITUTION_LABELS,
} from "../../../api/enrichedStakeholder";

import {
  getStakeholderById,
  getEnrichedStakeholderById,
} from "../logic/stakeholderSlice";

import {
  updateStakeholder,
  updateCustomerDetails,
  updateBusinessDetails,
} from "../logic/stakeholderThunks";

import { showErrorToast } from "../../../lib/toast";

interface BorrowerDetailsProps {
  stakeholderId: string;
}

const BorrowerDetails: React.FC<BorrowerDetailsProps> = ({
  stakeholderId,
}) => {
  const { id: caseId } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();

  const [editingField, setEditingField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const stakeholder = useAppSelector((state) =>
    getStakeholderById(state, stakeholderId)
  );

  const enrichedStakeholder = useAppSelector((state) =>
    getEnrichedStakeholderById(state, stakeholderId)
  );

  if (!stakeholder) {
    return null;
  }

  const customerDetails = enrichedStakeholder?.customer_details;
  const businessDetails = enrichedStakeholder?.business_details;

  const constitution = customerDetails?.constitution;

  const showBusinessFields =
    constitution !== undefined &&
    constitution !== null &&
    constitution !== BorrowerConstitution.INDIVIDUAL;

  const constitutionOptions = Object.values(BorrowerConstitution).map(
    (value) => ({
      name: BORROWER_CONSTITUTION_LABELS[value],
      value,
    })
  );

  const handleSave = async (
    field: string,
    value: string | null
  ): Promise<boolean> => {
    if (!caseId) return false;

    setIsSaving(true);
    const trimmedValue = value?.trim() || undefined;

    try {
      if (field === "name") {
        await dispatch(
          updateStakeholder({
            caseId,
            stakeholderId,
            request: { name: trimmedValue || "" },
          })
        ).unwrap();
      } else if (field === "constitution" || field === "cif_number") {
        await dispatch(
          updateCustomerDetails({
            caseId,
            stakeholderId,
            request: {
              cif_number: customerDetails?.cif_number,
              constitution: customerDetails?.constitution,
              [field]: trimmedValue,
            },
          })
        ).unwrap();
      } else {
        await dispatch(
          updateBusinessDetails({
            caseId,
            stakeholderId,
            request: {
              cin: businessDetails?.cin,
              gstin: businessDetails?.gstin,
              lei: businessDetails?.lei,
              line_of_activity: businessDetails?.line_of_activity,
              [field]: trimmedValue,
            },
          })
        ).unwrap();
      }

      setEditingField(null);
      return true;
    } catch {
      showErrorToast("Failed to update borrower details. Please try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SidepeekDetailsAccordion title="Details">
      <div className="flex w-full flex-col gap-2">
        <EditableField
          label="Name"
          value={stakeholder.name}
          type="text"
          isEditable={true}
          isEditing={editingField === "name"}
          onEditStart={() => setEditingField("name")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("name", value)}
          disabled={isSaving}
        />

        <EditableField
          label="Constitution"
          value={constitution ?? null}
          type="select"
          isEditable={true}
          isEditing={editingField === "constitution"}
          onEditStart={() => setEditingField("constitution")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("constitution", value)}
          options={constitutionOptions}
          displayValue={
            constitution ? BORROWER_CONSTITUTION_LABELS[constitution] : "--"
          }
          disabled={isSaving}
        />

        <EditableField
          label="Line of Activity"
          value={businessDetails?.line_of_activity ?? null}
          type="text"
          isEditable={true}
          isEditing={editingField === "line_of_activity"}
          onEditStart={() => setEditingField("line_of_activity")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("line_of_activity", value)}
          disabled={isSaving}
        />

        <EditableField
          label="CIF Number"
          value={customerDetails?.cif_number ?? null}
          type="text"
          isEditable={true}
          isEditing={editingField === "cif_number"}
          onEditStart={() => setEditingField("cif_number")}
          onEditCancel={() => setEditingField(null)}
          onSave={(value) => handleSave("cif_number", value)}
          disabled={isSaving}
        />

        {showBusinessFields && (
          <>
            <EditableField
              label="CIN"
              value={businessDetails?.cin ?? null}
              type="cin"
              isEditable={true}
              isEditing={editingField === "cin"}
              onEditStart={() => setEditingField("cin")}
              onEditCancel={() => setEditingField(null)}
              onSave={(value) => handleSave("cin", value)}
              disabled={isSaving}
            />

            <EditableField
              label="GSTIN"
              value={businessDetails?.gstin ?? null}
              type="gstin"
              isEditable={true}
              isEditing={editingField === "gstin"}
              onEditStart={() => setEditingField("gstin")}
              onEditCancel={() => setEditingField(null)}
              onSave={(value) => handleSave("gstin", value)}
              disabled={isSaving}
            />

            <EditableField
              label="LEI"
              value={businessDetails?.lei ?? null}
              type="lei"
              isEditable={true}
              isEditing={editingField === "lei"}
              onEditStart={() => setEditingField("lei")}
              onEditCancel={() => setEditingField(null)}
              onSave={(value) => handleSave("lei", value)}
              disabled={isSaving}
            />
          </>
        )}
      </div>
    </SidepeekDetailsAccordion>
  );
};

export default BorrowerDetails;